import React, { useEffect, useMemo, useRef } from 'react'
import { VirtuosoGrid, GridListProps } from 'react-virtuoso'
import { useWindowSize } from '@react-hook/window-size'
import { inject } from 'mobx-react'
import Album from './components/Album'
import { PageContent } from '../../components/Common/PageContent'
import { RectangleStackIcon as AddAlbumIcon } from '@heroicons/react/24/outline'
import { MODAL_STORE, ModalStore } from '../../stores/ModalStore'
import usePagination from '../../utils/usePagination'
import { API } from '../../services/api'
import AlbumModel from '../../models/album'
import { History } from 'history'
import { mobileBreakpoint } from '../../theme'
import { SectionHeader } from '../../components/SectionHeader/SectionHeader'
import { FetchingLoader } from '../../components/FetchingLoader'
import { Skeleton } from '../../components/Skeleton'
import { columnCountFor, skeletonCountFor } from './albumsGrid'
import { TwButton } from '../../components/Button/TwButton'
import { RouteComponentProps } from 'react-router'
import styled from 'styled-components'
import { WORKSPACES_STORE } from '../../stores'
import { WorkspacesStore } from '../../stores/WorkspaceStore'

const ALBUMS_PER_PAGE = 20

interface GridContext {
  skeletonCount: number
}

interface AlbumsScreenParams {
  workspaceSlug: string
}

interface AlbumScreenProps extends RouteComponentProps<AlbumsScreenParams> {
  [MODAL_STORE]: ModalStore
  [WORKSPACES_STORE]: WorkspacesStore
  history: History
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${mobileBreakpoint.width}px) {
    grid-template-columns: 1fr;
  }
`

const ItemContainer = styled.div`
  height: 300px;
`

const AlbumsScreen: React.FC<AlbumScreenProps> = (props) => {
  const workspacesStore = props[WORKSPACES_STORE]
  const workspaceId = workspacesStore.activeWorkspaceId
  const workspaceSlug = workspacesStore.activeWorkspaceSlug
  const [windowWidth] = useWindowSize()

  const fetchAlbumsPage = async (page: number, perPage: number) => {
    const response = await API.getInstance().getAlbums({
      workspaceId,
      count: perPage,
      from: page * perPage,
    })
    if (response.ok) {
      return response.data
    } else {
      throw new Error(JSON.stringify(response))
    }
  }

  const { data, isFetching, allFetched, fetchNext, reset } = usePagination<AlbumModel>({
    fetchPage: fetchAlbumsPage,
    perPage: ALBUMS_PER_PAGE,
  })

  // fetch first page and reset on workspace change
  useEffect(() => {
    if (!workspaceId) return
    reset()
    fetchNext().catch(() => {})
    // workspaceId too: the store may resolve it after the first render
  }, [workspaceSlug, workspaceId])

  // Virtuoso dedupes endReached by the last item index, so one dropped while a fetch was
  // in flight never comes back and the list stops loading. Remember it and replay instead.
  const endReachedRef = useRef(false)

  const loadMore = () => {
    if (allFetched) return
    if (isFetching) {
      endReachedRef.current = true
      return
    }
    fetchNext().catch(() => {})
  }

  useEffect(() => {
    if (!endReachedRef.current || isFetching || allFetched) return
    endReachedRef.current = false
    fetchNext().catch(() => {})
  }, [isFetching, allFetched])

  // Skeletons render as extra children of the grid itself, so CSS auto-placement fills the
  // last partial row before starting a new one. Their count rides on Virtuoso's `context`
  // so the List component keeps a stable identity and the grid is never remounted.
  const gridComponents = useMemo(
    () => ({
      List: React.forwardRef<HTMLDivElement, GridListProps & { context?: GridContext }>(
        ({ context, children, ...listProps }, ref) => (
          <GridContainer ref={ref} {...listProps}>
            {children}
            {Array.from({ length: context?.skeletonCount ?? 0 }, (_, i) => (
              <ItemContainer key={`skeleton-${i}`}>
                <div className="h-full w-full p-[10px]">
                  <Skeleton className="h-full w-full rounded-lg" />
                </div>
              </ItemContainer>
            ))}
          </GridContainer>
        )
      ),
      Item: ItemContainer,
    }),
    []
  )

  const gridContext: GridContext = {
    skeletonCount: isFetching ? skeletonCountFor(data.length, columnCountFor(windowWidth)) : 0,
  }

  return (
    <PageContent>
      <SectionHeader
        title="Twoje albumy"
        description="Miejsce na wszystkie Twoje wspomnienia i ich automatyczna organizacja! Dodawaj, wyszukuj, udostępniaj z każdego miejsca na świecie 🌍"
        actions={
          <TwButton
            variant="outline"
            icon={<AddAlbumIcon className="!h-4 !w-4" />}
            onClick={() => props.history.push(`/${workspaceSlug}/albums/add`)}
          >
            Stwórz nowy album
          </TwButton>
        }
      />
      <div className="pb-5">
        <VirtuosoGrid
          useWindowScroll
          data={data}
          context={gridContext}
          components={gridComponents}
          overscan={500}
          endReached={loadMore}
          itemContent={(index, album) => (
            <Album
              name={album.name}
              description={album.description}
              albumColor={album.color}
              photosCount={album.photos_count}
              coverPhotos={album.cover_photos}
              onClick={() => props.history.push(`/${workspaceSlug}/albums/${album.id}`)}
            />
          )}
        />
        {!isFetching && data.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <p className="text-lg">Brak albumów</p>
            <p className="text-sm">Stwórz swój pierwszy album, aby rozpocząć!</p>
          </div>
        )}
        {isFetching && <FetchingLoader />}
      </div>
    </PageContent>
  )
}

export default inject(WORKSPACES_STORE)(inject(MODAL_STORE)(AlbumsScreen))
